import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Header from "../components/Header";
import StickyFooter from "../components/StickyFooter";
import { getTransactions, exportTransactions } from "../utils/api";

const TransactionsPage = () => {
  const [search, setSearch] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Filters
  const [filters, setFilters] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    fetchTransactions(page, filters);
    // eslint-disable-next-line
  }, [page, filters]);

  const fetchTransactions = async (pageNum, appliedFilters) => {
    try {
      setLoading(true);
      const res = await getTransactions(pageNum, appliedFilters);
      setTransactions(res.data.data || []);
      setLastPage(res.data.last_page || 1);
    } catch (err) {
      console.error("Failed to load transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    await exportTransactions(filters);
  };

  // Filter transactions locally by reference (search box)
  const filteredTx = transactions.filter((tx) =>
    tx.reference?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Header />

      {/* Breadcrumb */}
      <Typography mt="60px" color="text.secondary" fontSize={13} mb={1}>
        Dashboard &gt; Transactions
      </Typography>

      {/* Title + Buttons */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        mb={2}
      >
        <Typography variant="h6" fontWeight={600}>
          Transactions
        </Typography>

        <Stack direction="row" spacing={1} mt={{ xs: 1, sm: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterOpen(true)}
          >
            Filter
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownloadIcon />}
            sx={{ textTransform: "none" }}
            onClick={handleExport}
          >
            Export
          </Button>
        </Stack>
      </Box>

      {/* Search Input */}
      <TextField
        placeholder="Search by transaction reference..."
        fullWidth
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1); // reset to first page when searching
        }}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Amount</strong>
              </TableCell>
              <TableCell>
                <strong>Type</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell>
                <strong>Reference</strong>
              </TableCell>
              <TableCell>
                <strong>Date</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography align="center" color="text.secondary" py={4}>
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredTx.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography align="center" color="text.secondary" py={4}>
                    No transactions found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTx.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell>${parseFloat(tx.amount).toFixed(2)}</TableCell>
                  <TableCell>{tx.type}</TableCell>
                  <TableCell>{tx.status}</TableCell>
                  <TableCell>{tx.reference}</TableCell>
                  <TableCell>
                    {new Date(tx.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination Controls */}
      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {/* Hide Previous button on first page */}
        {page > 1 ? (
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          >
            Previous
          </Button>
        ) : (
          <span />
        )}

        {/* Page indicator */}
        <Typography variant="body2" color="text.secondary">
          Page {page} of {lastPage}
        </Typography>

        {/* Hide Next button on last page */}
        {page < lastPage ? (
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        ) : (
          <span />
        )}
      </Box>

      {/* Filter Dialog */}
      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filter Transactions</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Status"
            fullWidth
            margin="normal"
            value={filters.status || ""}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </TextField>

          <TextField
            select
            label="Type"
            fullWidth
            margin="normal"
            value={filters.type || ""}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, type: e.target.value }))
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="credit">Credit</MenuItem>
            <MenuItem value="debit">Debit</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            Clear
          </Button>
          <Button
            onClick={() => {
              setPage(1); // reset pagination
              setFilterOpen(false);
            }}
            variant="contained"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <StickyFooter />
    </Box>
  );
};

export default TransactionsPage;